import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { PageLayout } from './layout'
import ReviewViewPrototype from './import-wizard/ReviewViewPrototype'

export default function TestProductConfig({ user, onLogout }) {
  const [showPrototype, setShowPrototype] = useState(false)

  // Sample data with PROPER domain model: Strain = mushroom variety, Product Type = quality grade
  const sampleProducts = [
    // Oyster mushroom variants (3 spelling variations)
    { Strain: 'Oyster', 'Product Type': 'A Class', recordCount: 45 },
    { Strain: 'oyster', 'Product Type': 'A Class', recordCount: 89 },
    { Strain: 'Oyster mushroom', 'Product Type': 'A class', recordCount: 12 },
    { Strain: 'Oyster', 'Product Type': 'B Class', recordCount: 23 },
    { Strain: 'oyster', 'Product Type': 'B class', recordCount: 15 },
    { Strain: 'Oyster', 'Product Type': 'Sample', recordCount: 8 },

    // Shiitake variants
    { Strain: 'Shiitake', 'Product Type': 'A Class', recordCount: 67 },
    { Strain: 'shiitake', 'Product Type': 'A class', recordCount: 12 },
    { Strain: 'Shiitake', 'Product Type': 'B Class', recordCount: 34 },

    // Lion's Mane variants (with apostrophe variations)
    { Strain: "Lion's Mane", 'Product Type': 'A Class', recordCount: 45 },
    { Strain: 'Lions Mane', 'Product Type': 'A class', recordCount: 11 },
    { Strain: "Lion's Mane", 'Product Type': 'Sample', recordCount: 5 },

    // Chestnut
    { Strain: 'Chestnut', 'Product Type': 'A Class', recordCount: 28 },
    { Strain: 'chestnut', 'Product Type': 'B Class', recordCount: 19 },
  ]

  return (
    <PageLayout
      title="Product Configuration Test"
      subtitle="Test strain-based hierarchical product grouping"
      user={user}
      onLogout={onLogout}
    >
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-semibold mb-4">Hierarchical Product Configuration Test</h1>
          <p className="text-gray-600 mb-6">
            Test the new strain-based hierarchical product grouping. This groups products by mushroom strain first, then by quality grade.
          </p>

          <Button onClick={() => setShowPrototype(true)} className="mb-6">
            Show Product Review Prototype
          </Button>

          <div className="grid grid-cols-2 gap-6">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h2 className="font-semibold mb-2">Sample Mushroom Strains:</h2>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• <strong>Oyster</strong> - 3 spelling variants (Oyster, oyster, Oyster mushroom)</li>
                <li>• <strong>Shiitake</strong> - 2 variants (Shiitake, shiitake)</li>
                <li>• <strong>Lion's Mane</strong> - 2 variants (Lion's Mane, Lions Mane)</li>
                <li>• <strong>Chestnut</strong> - 2 variants (Chestnut, chestnut)</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h2 className="font-semibold mb-2">Quality Grades:</h2>
              <ul className="text-sm space-y-1 text-gray-700">
                <li>• <strong>A Class</strong> - Premium quality</li>
                <li>• <strong>B Class</strong> - Standard quality</li>
                <li>• <strong>Sample</strong> - Free samples</li>
              </ul>
              <p className="text-xs text-gray-500 mt-2">Note: Grade spellings also vary (e.g., "A Class" vs "A class")</p>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-semibold text-blue-900 mb-2">How the Hierarchical View Works:</h3>
            <ol className="text-sm space-y-1 text-blue-800 list-decimal list-inside">
              <li><strong>Left Panel</strong>: Select a mushroom strain (variants are automatically grouped)</li>
              <li><strong>Middle Panel</strong>: See all quality grades for that strain</li>
              <li><strong>Right Panel</strong>: View product details and configuration</li>
              <li><strong>Variant Detection</strong>: Shows how many spelling variations were found and grouped</li>
            </ol>
          </div>
        </div>

      {showPrototype && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <h2 className="text-lg font-semibold">Product Review - Strain Hierarchy</h2>
              <Button variant="ghost" onClick={() => setShowPrototype(false)}>
                Close
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <ReviewViewPrototype products={sampleProducts} />
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  )
}
